import axios from 'axios';
import { constants } from '../../constants';
import { Profile } from '../../contexts/AdminUserContext';
import { ActivityTemplate, AdminMember, Task, Intro, TemplateTag } from '../../models';

let cachedTemplateTags: TemplateTag[] | null = null;
class AdminService {
  private defaultHeaders: Record<string, string> = {};
  private endpoint = `${constants.endpoint}/admin`;

  constructor() {
    const token = localStorage.getItem(constants.adminTokenKey);
    this.defaultHeaders = {
      Authorization: `bearer ${token}`,
    };
  }

  private updateToken(token: string): void {
    this.defaultHeaders = {
      Authorization: `bearer ${token}`,
    };
  }

  auth(email: string, password: string): Promise<{ token: string }> {
    return axios
      .post<Profile>(
        `${this.endpoint}/auth`,
        {
          email,
          password,
        },
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      )
      .then((axiosRes) => {
        const data = axiosRes.data;
        if (data.token) {
          localStorage.setItem(constants.adminTokenKey, data.token);
          localStorage.setItem(constants.adminEmailKey, data.email);
          this.updateToken(data.token);
        }
        return data;
      });
  }

  me(): Promise<{ email: string }> {
    return axios
      .get<{ email: string }>(`${this.endpoint}/me`, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .then((axiosRes) => {
        return axiosRes.data;
      })
      .catch(() => {
        return { email: '' };
      });
  }

  getActivityTemplates(): Promise<ActivityTemplate[]> {
    return axios
      .get<{ templates: ActivityTemplate[] }>(`${this.endpoint}/template`, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .then((axiosRes) => {
        return axiosRes.data.templates;
      })
      .catch(() => {
        return [];
      });
  }

  getTemplateTasks(templateId: string): Promise<Task[]> {
    return axios
      .get<{ result: Task[] }>(`${this.endpoint}/template/${templateId}/task`, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .then((axiosRes) => {
        return axiosRes.data.result;
      })
      .catch(() => {
        return [];
      });
  }

  createTemplate(body: FormData): Promise<{ id: string }> {
    return axios
      .post<{ id: string }>(`${this.endpoint}/template`, body, {
        headers: {
          ...this.defaultHeaders,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((axiosRes) => axiosRes.data)
      .catch((axiosErr) => {
        throw new Error(axiosErr.response.data.message);
      });
  }

  updateTemplate(body: FormData): Promise<any> {
    return axios
      .patch<any>(`${this.endpoint}/template`, body, {
        headers: {
          ...this.defaultHeaders,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((axiosRes) => axiosRes.data)
      .catch((axiosErr) => {
        throw new Error(axiosErr.response.data.message);
      });
  }

  updateTemplateStatus(body: { id: string; status: string }) {
    return axios
      .patch<any>(`${this.endpoint}/template/status`, body, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .then((axiosRes) => axiosRes.data)
      .catch((axiosErr) => {
        throw new Error(axiosErr.response.data.message);
      });
  }

  getAdminMembers(): Promise<AdminMember[]> {
    return axios
      .get<{ result: AdminMember[] }>(`${this.endpoint}/member`, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .then((axiosRes) => {
        return axiosRes.data.result;
      })
      .catch(() => {
        return [];
      });
  }

  createAdminMember(body: {
    username: string;
    email: string;
    password: string;
  }): Promise<any> {
    return axios
      .post(`${this.endpoint}/member`, body, {
        headers: {
          ...this.defaultHeaders,
        },
      })
      .catch((axiosErr) => {
        throw new Error(axiosErr.response.data.message);
      });
  }

  deleteAdminMember(data: { email: string }): Promise<any> {
    return axios
      .request({
        method: 'DELETE',
        url: `${this.endpoint}/member`,
        data,
        headers: {
          ...this.defaultHeaders,
        },
      })
      .catch((axiosErr) => {
        throw new Error(axiosErr.response.data.message);
      });
  }

  getTemplateIntros(templateId: string): Promise<Intro[]> {
    if (!templateId) {
      return new Promise((resolve) => resolve([]));
    }

    return axios
      .get<{ result: Intro[] }>(
        `${this.endpoint}/template/${templateId}/intro`,
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      )
      .then((axiosRes) => {
        return axiosRes.data.result;
      })
      .catch(() => {
        return [];
      });
  }

  getTemplateTags = async (refreshCache: boolean = false) => {
    if (refreshCache) {
      cachedTemplateTags = null
    }
    if (!cachedTemplateTags) {
      const res = await axios.get<{ tags: TemplateTag[] }>(
        `${this.endpoint}/tags`,
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      );

      cachedTemplateTags = res.data.tags;
    }
    return cachedTemplateTags;
  }
}

const adminService = new AdminService();
export default adminService;
